import React, { useState } from 'react';
import { Filter, AlertCircle } from 'lucide-react';
import { ItemCard } from '../cards/ItemCard';
import { useItems } from '../../../hooks/useItems';
import type { User } from '../../../types/user';
import type { ItemRarity, ItemType } from '../../../types/items';

interface ItemMarketProps {
  user: User | null;
}

export function ItemMarket({ user }: ItemMarketProps) {
  const { items, loading, error, refetch } = useItems();
  const [selectedRarity, setSelectedRarity] = useState<ItemRarity | 'all'>('all');
  const [selectedType, setSelectedType] = useState<ItemType | 'all'>('all');

  const filteredItems = items.filter(item => 
    (selectedRarity === 'all' || item.rarity === selectedRarity) &&
    (selectedType === 'all' || item.type === selectedType)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={refetch}
          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-4 items-center">
        <Filter className="w-5 h-5 text-gray-400" />
        <select
          value={selectedRarity}
          onChange={(e) => setSelectedRarity(e.target.value as ItemRarity | 'all')}
          className="bg-gray-800 text-gray-300 px-3 py-2 rounded-lg"
        >
          <option value="all">All Rarities</option>
          <option value="common">Common</option>
          <option value="uncommon">Uncommon</option>
          <option value="rare">Rare</option>
          <option value="epic">Epic</option>
          <option value="legendary">Legendary</option>
        </select>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value as ItemType | 'all')}
          className="bg-gray-800 text-gray-300 px-3 py-2 rounded-lg"
        >
          <option value="all">All Types</option>
          <option value="weapon">Weapons</option>
          <option value="armor">Armor</option>
          <option value="accessory">Accessories</option>
          <option value="food">Food</option>
        </select>
      </div>

      {filteredItems.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No items found matching your filters
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              user={user}
              onPurchaseComplete={refetch}
            />
          ))}
        </div>
      )}
    </div>
  );
}